import React, { useState } from "react";
import { FaTimes, FaCheckCircle } from "react-icons/fa";
import "../styles/Quotation.css";

// Constants
const EMAIL = "securedev001@gmail.com";

const PROJECT_SCOPES = [
  "Small (1 month or less)",
  "Medium (1-3 months)",
  "Large (3+ months)",
  "Ongoing Support",
];

const BUDGET_RANGES = [
  "Less than $1,000",
  "$1,000 - $5,000",
  "$5,000 - $10,000",
  "$10,000 - $25,000",
  "$25,000+",
];

const TIMELINES = [
  "ASAP (1-2 weeks)",
  "1 Month",
  "2-3 Months",
  "3+ Months (Flexible)",
];

const INITIAL_FORM_STATE = {
  name: "",
  email: "",
  phone: "",
  company: "",
  projectScope: "",
  budget: "",
  timeline: "",
  details: "",
};

// Subcomponents
const FormField = ({
  label,
  type = "text",
  id,
  name,
  value,
  onChange,
  placeholder,
  required,
}) => (
  <div className="form-group">
    <label htmlFor={id}>
      {label}
      {required && <span className="required">*</span>}
    </label>
    <input
      type={type}
      id={id}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      aria-required={required}
    />
  </div>
);

const SelectField = ({
  label,
  id,
  name,
  value,
  onChange,
  options,
  required,
}) => (
  <div className="form-group">
    <label htmlFor={id}>
      {label}
      {required && <span className="required">*</span>}
    </label>
    <select
      id={id}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      aria-required={required}
    >
      <option value="">Select {label.toLowerCase()}...</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  </div>
);

const TextAreaField = ({
  label,
  id,
  name,
  value,
  onChange,
  placeholder,
  required,
  rows = 5,
}) => (
  <div className="form-group full-width">
    <label htmlFor={id}>
      {label}
      {required && <span className="required">*</span>}
    </label>
    <textarea
      id={id}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      rows={rows}
      aria-required={required}
    />
  </div>
);

const QuotationForm = ({ service, formData, onChange, onSubmit }) => (
  <>
    <div className="quotation-header">
      <h2>Request a Quote</h2>
      <p className="service-type">
        Service: <span className="service-name">{service}</span>
      </p>
    </div>

    <form className="quotation-form" onSubmit={onSubmit}>
      {/* Row 1: Name & Email */}
      <div className="form-row">
        <FormField
          label="Full Name"
          id="name"
          name="name"
          value={formData.name}
          onChange={onChange}
          placeholder="John Doe"
          required
        />

        <FormField
          label="Email Address"
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={onChange}
          placeholder="john@example.com"
          required
        />
      </div>

      {/* Row 2: Phone & Company */}
      <div className="form-row">
        <FormField
          label="Phone Number"
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={onChange}
          placeholder="+254 743 678 908"
        />

        <FormField
          label="Company Name"
          id="company"
          name="company"
          value={formData.company}
          onChange={onChange}
          placeholder="Your Company"
        />
      </div>

      {/* Row 3: Project Scope & Budget */}
      <div className="form-row">
        <SelectField
          label="Project Scope"
          id="projectScope"
          name="projectScope"
          value={formData.projectScope}
          onChange={onChange}
          options={PROJECT_SCOPES}
          required
        />

        <SelectField
          label="Budget Range"
          id="budget"
          name="budget"
          value={formData.budget}
          onChange={onChange}
          options={BUDGET_RANGES}
        />
      </div>

      {/* Timeline */}
      <SelectField
        label="Desired Timeline"
        id="timeline"
        name="timeline"
        value={formData.timeline}
        onChange={onChange}
        options={TIMELINES}
        required
      />

      {/* Project Details */}
      <TextAreaField
        label="Project Details & Requirements"
        id="details"
        name="details"
        value={formData.details}
        onChange={onChange}
        placeholder="Tell us about your project goals, requirements, and specific features..."
        required
      />

      {/* Submit Button */}
      <button type="submit" className="quotation-submit">
        Send Quotation Request
      </button>

      <p className="form-note">
        This will open your email client with the quotation details pre-filled
      </p>
    </form>
  </>
);

const QuotationSuccess = () => (
  <div className="quotation-success">
    <FaCheckCircle className="success-icon" aria-hidden="true" />
    <h3>Quotation Ready ✓</h3>
    <p>Your quotation has been prepared and saved.</p>
    <p className="success-detail">
      If your email didn't open, you can send it manually to:{" "}
      <strong>{EMAIL}</strong>
    </p>
  </div>
);

// Main Component
/**
 * Quotation Modal Component
 * Collects quotation details and opens user's email client with pre-filled details
 */
function Quotation({ service, onClose }) {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * Saves quotation to localStorage as backup
   */
  const saveToLocalStorage = (data) => {
    try {
      const quotations = JSON.parse(localStorage.getItem("quotations")) || [];
      const newQuotation = {
        id: Date.now(),
        timestamp: new Date().toLocaleString(),
        service,
        ...data,
        status: "sent",
      };
      quotations.push(newQuotation);
      localStorage.setItem("quotations", JSON.stringify(quotations));
      console.log("✓ Quotation saved to localStorage:", newQuotation);
    } catch (err) {
      console.error("localStorage error:", err);
    }
  };

  /**
   * Creates mailto link with pre-filled quotation details
   */
  const createMailtoLink = (data) => {
    const subject = `New Quotation Request from ${data.name}`;

    const body = `Client Information:
Name: ${data.name}
Email: ${data.email}
Phone: ${data.phone || "Not provided"}
Company: ${data.company || "Not specified"}

Project Details:
Service: ${service}
Project Scope: ${data.projectScope}
Budget Range: ${data.budget || "Not specified"}
Timeline: ${data.timeline}

Requirements:
${data.details}

---
Sent from Engineer K Quotation Form`;

    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(body);

    const mailtoLink = `mailto:${EMAIL}?subject=${encodedSubject}&body=${encodedBody}`;

    console.log("📧 Mailto link created:", mailtoLink);
    return mailtoLink;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    console.log("📝 Form submitted with data:", formData);

    // Save to localStorage
    saveToLocalStorage(formData);

    // Create mailto link
    const mailtoLink = createMailtoLink(formData);

    // Open email client
    console.log("🔗 Opening mailto link...");
    window.location.href = mailtoLink;

    // Show success message
    setSubmitted(true);
    setFormData(INITIAL_FORM_STATE);

    // Close modal after 4 seconds
    setTimeout(() => {
      onClose();
      setSubmitted(false);
    }, 4000);
  };

  return (
    <div
      className="quotation-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="quotation-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="close-button"
          onClick={onClose}
          title="Close quotation form"
          aria-label="Close"
        >
          <FaTimes aria-hidden="true" />
        </button>

        {!submitted ? (
          <QuotationForm
            service={service}
            formData={formData}
            onChange={handleChange}
            onSubmit={handleSubmit}
          />
        ) : (
          <QuotationSuccess />
        )}
      </div>
    </div>
  );
}

export default Quotation;
